/**
 * 藝品印刷訂單(REQ-20260806003)—— 可直接列印 / 另存 A4 PDF 的訂單版面。
 *
 * 這張訂單**不是新的資料表**:每一格都來自既有的 `SalesOrder` + `SalesOrderLine`
 * (REQ-20260806001 §3.4 的結論)。另開一張表會變成第二份真相 —— 應收單改了金額,
 * 印刷訂單不會跟著改。所以這裡是「同一筆資料的另一種呈現」,不是另一筆資料。
 *
 * 沒有引進 PDF 套件:走瀏覽器列印(Cmd+P → 另存 PDF),與月結對帳單同一套做法。
 * 列印樣式在 `styles/globals.css` 的 `@media print`,只留 `.print-area`。
 *
 * 紙張方向(REQ-20260806004):本頁要 A4 **橫向**單面,但 `@page` 是全域規則,
 * 改 globals.css 會連月結對帳單(REQ-20260806002)一起翻成橫的。所以覆寫寫在
 * 下面那個 `<style>`,只在本頁掛載期間存在,離開路由就消失。
 *
 * 「印刷顏色 / 稿件處理 / 出貨方式」原本 schema 沒有對應資料、印成空格讓現場手寫,
 * REQ-20260809002 起改成建單時就填(`SalesOrder` 上的三個欄位);
 * 「印刷面別」(單面 / 雙面)是 REQ-20260810003 補上的第四個同類欄位。
 * 「帳款月份」則是從既有的 `txnDate` 衍生,刻意沒有開自己的欄位 ——
 * 見下方 `billingPeriod()`。歷史單這四格仍然是空的(舊資料沒有這些值)。
 *
 * 上半部三欄的分組(REQ-20260825007):左 = 稿樣圖、中 = **商品資訊**(生產 / 美工看的)、
 * 右 = **客戶與帳款資訊**(會計 / 請款看的)。在此之前商品欄位與帳款欄位混在中 / 右兩欄,
 * 看單的人要來回找。分組後兩欄列數不齊(中 9 列 / 右 10 列)是**刻意的** ——
 * 客戶要把中欄底部多出來的空間留給「加工明細」,加工流程多的商品才容納得下,
 * 所以加工明細排在中欄最後一格,不要為了「對齊」把它往上搬。
 *
 * 多品項單的規則:line 層級的欄位逐 line 重複,訂單層級的只印一次。
 * 「印刷顏色 / 印刷面別 / 稿件處理」是 `SalesOrder` 上的單一值(不屬於某一行品項),
 * 只跟著**第一個**品項印一次,插在商品材質與加工明細之間 —— 也就是客戶指定的位置;
 * 「商品數量 / 商品單價」則是 line 層級,每個品項都印。
 *
 * REQ-20260824009 起工具列多一顆「修改訂單」:訪談頁的單號徽章改成**先開這一頁**,
 * 看過內容才決定要不要改 / 要不要印,所以改單的入口得在這裡。已收款的單那顆按鈕
 * 反灰按不動(規則與出貨管理列表共用 `order-lock.ts`)。
 */
import { Fragment, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { SalesOrderDetail } from '@app/shared';
import { ExportJpegButton } from '../../components/ExportJpegButton';
import { buildDocumentFilename } from '../../lib/filename';
import { formatMoney } from '../../lib/format-money';
import { useAttachmentUrl } from '../attachments/api';
import { useSalesOrder } from '../accounting/api';
import { isOrderSettled } from './order-lock';

const TAX_MODE_LABEL: Record<SalesOrderDetail['taxMode'], string> = {
  EXCLUSIVE: '外加 5%',
  INCLUSIVE: '內含',
  EXEMPT: '免稅',
};

export function OrderPrintPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const orderId = id ? Number(id) : undefined;
  const { data: order, isLoading, isError, error } = useSalesOrder(orderId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /*
   * 「關閉頁面」(REQ-20260809001)現在有兩條入口要應付:
   *  - 出貨列表的 `target="_blank"` 列印連結 → 新分頁,session history 只有一筆,
   *    `window.close()` 關得掉。
   *  - 「儲存修改」後**同分頁導覽**進來(REQ-20260811005)→ 這種分頁瀏覽器不讓腳本關,
   *    按下去完全沒反應,使用者會卡在列印頁。改成導回出貨管理清單。
   *
   * 判斷用的是 session history 長度,**不是 `window.opener`** —— 列印連結帶
   * `rel="noreferrer"`,新分頁的 opener 一律是 null,用它判斷會把兩條路徑都當成
   * 同分頁導覽,反而把原本關得掉的分頁變成關不掉。而「history 只有一筆」正好就是
   * HTML 規範裡 `window.close()` 關得掉的條件之一。
   *
   * 導回的目的地仍然是**出貨管理清單**,不是 `navigate(-1)`(REQ-20260811005 §四:
   * 上一頁是這張單的編輯表單,回去等於再開一次已存檔的舊 state)。
   * REQ-20260820006 只多一件事:清單頁把使用者離開前的篩選條件放進 `?from=`,
   * 有帶就回那份清單、沒帶(直接改網址列進來、舊連結)就回預設的出貨管理分頁。
   *
   * REQ-20260825008 起 `/customers/console` 也算數:訪談頁的單號徽章會帶著
   * 那位客戶的詳細頁進來,從客戶頁點進來的人要回的是那位客戶,不是會計的清單。
   * 仍然是白名單(不是「有帶就跳」)—— 網址是使用者可改的輸入,這顆按鈕的語意
   * 是「回我剛才那一頁」,不是任意跳轉。
   */
  const closePage = (): void => {
    if (window.history.length <= 1) {
      window.close();
      return;
    }
    const from = searchParams.get('from');
    const isTrustedFrom = from?.startsWith('/accounting') || from?.startsWith('/customers/console');
    navigate(isTrustedFrom ? from! : '/accounting?tab=sales');
  };

  /*
   * Cmd+P → 另存 PDF 時,存檔對話框的預設檔名就是分頁標題。整個 SPA 只有 index.html
   * 設了固定的「訪談系統」,所以每張訂單存出來都叫同一個名字。改成單據自己的名字,
   * 存檔時不用再手打(REQ-20260811002)。
   *
   * REQ-20260903006 起檔名從單純的 `orderNo` 換成「表單類別-客戶簡稱-訂單編號」
   * (例:`訂單-金樂印-B260903005`)—— 使用者收到的是一疊各家客戶的單,
   * 只有編號的檔名在檔案總管裡認不出是誰的。**effect 本身的三個做法沒有變**:
   * 掛在早退之前、cleanup 還原成進頁前的值、依賴掛在那個字串上而不是 `order` 物件。
   */
  const documentFilename = order
    ? buildDocumentFilename(['訂單', order.customerSimpleName, order.orderNo])
    : '';
  useEffect(() => {
    if (!documentFilename) return;
    const previousTitle = document.title;
    document.title = documentFilename;
    return () => {
      document.title = previousTitle;
    };
  }, [documentFilename]);

  /** 「另存 JPEG」要截的那一塊:底下 `OrderSheet` 的 `.print-area`(REQ-20260903006) */
  const sheetRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return (
      <div role="status" className="text-slate-500">
        載入中...
      </div>
    );
  }
  if (isError || !order) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </div>
    );
  }

  /*
   * 「修改訂單」的鎖(REQ-20260824009)。判斷用的是出貨管理列表同一支
   * `isOrderSettled()`,不在這裡重寫一次 `Number(...) > 0`。
   */
  const locked = isOrderSettled(order.settledAmount);

  return (
    <div className="grid gap-4" ref={sheetRef}>
      <style>{'@page { size: A4 landscape; }'}</style>

      <div className="flex items-center gap-3 no-print">
        {/* 同一份單據的第二種存檔格式(REQ-20260903006);PDF 那條路一行未動 */}
        <ExportJpegButton targetRef={sheetRef} filename={documentFilename} />
        <button
          type="button"
          onClick={() => window.print()}
          className="border border-blue-600 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 text-sm"
          data-testid="order-print-btn"
        >
          列印 / 存 PDF
        </button>
        {/*
         * 「修改訂單」(REQ-20260824009)。訪談頁的單號徽章現在**先開這一頁** ——
         * 使用者的說法是「沒有看過內容怎會知道是否需要列印」,所以看完內容之後
         * 要改單的入口就得在這裡,不能只有出貨管理清單那一個。
         *
         * **同分頁導覽**:這裡已經在 SPA 路由裡,再開一個新分頁只是多一個要收的分頁。
         * 目的地就是既有的編輯深連結(`?editOrderId=`,REQ-20260811001 開的那條)。
         *
         * 目前網址上的 `?from=` 要**原封不動轉交**給編輯頁(REQ-20260825008)——
         * 使用者是從哪裡進來的,存完檔就該回到哪裡。少了這一段,從客戶的訪談紀錄
         * 點進來改單的人,按下「儲存修改」會被丟到會計的出貨管理清單。
         *
         * 已收過款的單:按鈕**留著但按不動**(需求原文「按鍵就不能有任何作用反應」)。
         * 用原生 `disabled` 而不是「按了跳警告」—— 前者連 onClick 都不會觸發。
         * 反灰的同時把原因與下一步寫進 `title`,比照 REQ-20260821001 §2.1 的既有慣例:
         * 不要讓人不知道為什麼不能按。
         */}
        <button
          type="button"
          disabled={locked}
          onClick={() => {
            const from = searchParams.get('from');
            const target = `/accounting?tab=sales&editOrderId=${order.id}`;
            navigate(from ? `${target}&from=${encodeURIComponent(from)}` : target);
          }}
          title={
            locked
              ? '這張單已經收過款,已鎖定為唯讀。要修改請先到「應收沖帳」找到對應的收款紀錄按「⚠️ 解除沖銷」'
              : '回到出貨管理的編輯畫面修改這張單'
          }
          className="border border-blue-600 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 text-sm disabled:opacity-50 disabled:border-slate-300 disabled:text-slate-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          data-testid="order-print-edit-btn"
        >
          修改訂單
        </button>
        {/* 新分頁就關掉、同分頁導覽進來就導回清單,判斷寫在上面的 closePage() */}
        <button
          type="button"
          onClick={closePage}
          className="bg-slate-200 text-slate-700 px-3 py-1.5 rounded hover:bg-slate-300 text-sm"
          data-testid="order-print-close-btn"
        >
          關閉頁面
        </button>
        <span className="text-slate-500 text-xs">
          列印時請選 A4、紙張方向橫向;建單時沒填的格子會留白供現場手寫
        </span>
      </div>

      <OrderSheet order={order} />
    </div>
  );
}

function OrderSheet({ order }: { order: SalesOrderDetail }): JSX.Element {
  return (
    <div
      // max-w-6xl ≈ A4 橫向的長寬比,螢幕上看到的比例就接近印出來的樣子
      className="print-area mx-auto bg-white text-black shadow-sm rounded-md p-6 text-sm w-full max-w-6xl"
      data-testid="order-print-sheet"
    >
      <header className="border-b border-black pb-2">
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl font-bold tracking-widest">藝品印刷訂單</h1>
          {/*
           * 右上角印的是訂單日期(REQ-20260825001),而且這裡是全張單**唯一**印訂單日期的地方
           * —— 中欄原本重複的那一格已於 REQ-20260825005 移除。
           * 訂單編號沒有消失,它本來就在中欄那一格。
           */}
          <span className="text-xs text-slate-600">{order.txnDate}</span>
        </div>
      </header>

      {/*
       * 橫向紙張:上排三欄(稿樣 / 商品資訊 / 客戶與帳款資訊),把高度換成寬度才印得下單頁。
       * 欄數刻意不掛 `md:` —— 列印時瀏覽器不保證會把 min-width 媒體查詢算成紙張寬度
       * (實測 Chrome 列印會退回單欄堆疊,整張單就變成兩頁)。這是列印用的固定版面。
       */}
      <div className="mt-3 grid gap-3 grid-cols-3">
        {/* 左:稿樣區(「商品稿件擺放位置區」那一列標籤已於 REQ-20260825001 移除,只留外框) */}
        <section className="border border-black">
          {/*
           * 稿樣區是整張單最該看清楚的地方,高度盡量給它 —— 但不能給到爆頁:
           * REQ-20260806004 的 h-96 讓版面總高 716px,而 Chrome 預設邊界只有 717px 可印,
           * 只剩 1px 餘裕 → 使用者實際列印(預設開頁首頁尾)就被擠出第二頁。
           * h-80 讓總高降到 636px,仍比 REQ-20260806003 原本的 h-72 大(REQ-20260807004)。
           */}
          <div className="h-80 flex flex-wrap items-center justify-center content-center gap-2 p-2">
            {order.artworks.length > 0 ? (
              <ArtworkGrid artworks={order.artworks} />
            ) : (
              <span className="text-slate-400 text-xs">未指定稿樣</span>
            )}
          </div>
          {/*
           * 多張稿樣時檔名逐一列出,現場才對得出紙上是哪幾個檔。
           *
           * 這一格刻意**不用** `Cell`(REQ-20260825001):客戶在附圖上單獨圈了它,
           * 但其餘二十幾格維持原樣 —— 給 `Cell` 加一個字級參數,等於為了一處呼叫
           * 讓所有呼叫都多背一個旗標。就地攤平成同樣結構的兩個 span 比較誠實。
           * 資料來源與「、」串接的行為與改動前完全相同,只有字級變了。
           *
           * 字級 `text-[0.6rem]`(9.6px)是 REQ-20260825002 改的:上一版放成 `text-sm`
           * 客戶嫌太大,要求縮成約三分之一。**這個數字不能隨手調** —— 標籤在 `w-28`
           * 扣掉 `px-2` 只剩 95px,實測 `0.65rem` 需要 95.3px 就會把「稱」擠成孤字
           * (差 0.3px),`0.6rem` 只要 88px,留 7px 給客戶端字型差異。
           * 字重是一般字重,**刻意不加粗**:REQ-20260825002 曾留下 `font-semibold` /
           * `font-bold` 讓這格縮小後仍醒目,客戶在 REQ-20260825005 明說「這個地方僅
           * 輔助作用,不用特別強調明顯」,所以字重拿掉了 —— 別再加回來。
           *
           * 沒有上下框線是原本就有的樣子(`Cell` 的 border-b 被 `last:` 消掉),
           * 這裡照抄,不順手補線。
           */}
          <div className="flex min-h-[2rem]">
            <span className="w-28 shrink-0 border-r border-black px-2 py-1 text-[0.6rem] bg-slate-50">
              檔案位置 / 檔案名稱
            </span>
            <span className="px-2 py-1 break-all text-[0.6rem]">
              {order.artworks.length > 0
                ? order.artworks.map((a) => a.originalName).join('、')
                : ''}
            </span>
          </div>
        </section>

        {/*
         * 中:商品資訊(生產 / 美工看的那一半,REQ-20260825007)。
         * 「印刷顏色 / 印刷面別 / 稿件處理」是訂單層級的單一值,所以只跟著第一個品項
         * 印一次 —— 位置就是客戶指定的「商品材質之後、加工明細之前」。
         * 加工明細固定排在最後一格,底下留的空間是客戶要來容納多道加工流程的。
         */}
        <section className="border border-black">
          {order.lines.map((line, i) => (
            <div key={line.id}>
              <Cell label="商品編號" value={line.productCode} />
              <Cell label="貨品類別" value={line.productCategoryName} />
              <Cell label="商品名稱" value={line.productName} />
              <Cell label="商品規格" value={line.productSpec} />
              <Cell label="商品材質" value={line.productMaterial} />
              {i === 0 && (
                <>
                  <Cell label="印刷顏色" value={order.printColor} />
                  <Cell label="印刷面別" value={order.printSides} />
                  <Cell label="稿件處理" value={order.artworkHandling} />
                </>
              )}
              <Cell label="加工明細" value={line.productProcessing} />
            </div>
          ))}
        </section>

        {/*
         * 右:客戶與帳款資訊(會計 / 請款看的那一半,REQ-20260825007)。
         * 建單時沒填的格子仍然留白給現場手寫。
         *
         * 品項的數量 / 單價夾在訂單層級的格子中間,所以這裡用 `Fragment` 而不是像中欄
         * 那樣包 `<div>` —— 包起來的話 `Cell` 的 `last:border-b-0` 會落在「商品單價」上,
         * 把它與「出貨方式」之間的橫線吃掉。中欄的品項格在 section 最尾端,不受影響。
         */}
        <section className="border border-black">
          <Cell label="客戶編號" value={order.customerCode} />
          <Cell label="客戶簡稱" value={order.customerSimpleName} />
          <Cell label="訂單編號" value={order.orderNo} />
          <Cell label="帳款月份" value={billingPeriod(order.txnDate)} />
          {order.lines.map((line) => (
            <Fragment key={line.id}>
              <Cell label="商品數量" value={`${line.qty}${line.unitName ?? ''}`} />
              <Cell label="商品單價" value={line.unitPrice} />
            </Fragment>
          ))}
          <Cell label="出貨方式" value={order.shippingMethod} />
          <Cell label="請款憑證" value={order.customerPaymentCert} />
          <Cell label="請款方式" value={order.customerPaymentMethod} />
          {/* 標籤由「稅別」改成「課稅類別」(REQ-20260825007),對照表與三個值的內容未動 */}
          <Cell label="課稅類別" value={TAX_MODE_LABEL[order.taxMode]} />
        </section>
      </div>

      <div className="mt-3 grid gap-3 grid-cols-3">
        {/* 備註(REQ-20260825002:標籤原本是「備註 / 營運所分開」,客戶要求只留「備註」) */}
        <section className="border border-black col-span-2">
          <div className="border-b border-black px-2 py-1 text-xs font-medium">備註</div>
          {/* 8rem → 7rem:同上,單頁需要的餘裕由稿樣區出大部分,這裡再讓 16px(REQ-20260807004) */}
          <div className="p-2 min-h-[7rem] whitespace-pre-wrap" data-testid="order-print-notes">
            {order.notes ?? ''}
          </div>
        </section>

        {/* 金額統計區 */}
        <section className="border border-black" data-testid="order-print-amounts">
          <div className="border-b border-black px-2 py-1 text-xs font-medium">金額統計區</div>
          <dl className="p-2 grid gap-1">
            <Amount label="未稅金額" value={order.subtotal} />
            <Amount label="稅額" value={order.tax} />
            <div className="flex items-baseline justify-between border-t border-black pt-1">
              <dt className="text-sm">含稅總計</dt>
              <dd className="text-2xl font-bold text-red-600 font-mono">
                {formatMoney(order.total)}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  );
}

/**
 * 稿樣區的多張圖片(REQ-20260809004)。
 *
 * 框的高度不能跟著張數變高 —— 整張單就是靠這個固定高度維持單頁列印
 * (REQ-20260807004 為了 1px 的餘裕把 h-96 降成 h-80)。所以改成把框裡的空間
 * 分給每張圖:兩欄排版,高度按列數等分。勾幾張都不會擠爆版面,也不會被裁掉;
 * 張數多時每張自然變小,這是固定紙張下唯一誠實的取捨。
 */
function ArtworkGrid({ artworks }: { artworks: SalesOrderDetail['artworks'] }): JSX.Element {
  const cols = artworks.length > 1 ? 2 : 1;
  const rows = Math.ceil(artworks.length / cols);
  // 百分比要隨張數算,Tailwind 的 JIT 掃不到執行期字串,所以用 inline style。
  // 扣掉 0.5rem 是 flex 的 gap-2,否則兩張各佔 50% 會被 gap 擠到換行。
  const limit = {
    maxWidth: `calc(${100 / cols}% - 0.5rem)`,
    maxHeight: `calc(${100 / rows}% - 0.5rem)`,
  };
  return (
    <>
      {artworks.map((a) => (
        <ArtworkImage key={a.uuid} artwork={a} limit={limit} />
      ))}
    </>
  );
}

/** signed URL 要逐張取,所以每張圖包成一個元件(hook 不能寫在迴圈裡)。 */
function ArtworkImage(props: {
  artwork: SalesOrderDetail['artworks'][number];
  limit: { maxWidth: string; maxHeight: string };
}): JSX.Element {
  const { artwork, limit } = props;
  const { data: url } = useAttachmentUrl(artwork.uuid);
  if (!url) return <span className="text-slate-400 text-xs">稿樣載入中…</span>;
  return (
    <img
      src={url}
      alt={artwork.originalName}
      // 等比例縮放,不裁切也不變形
      className="object-contain"
      style={limit}
      data-testid="order-print-artwork"
    />
  );
}

/**
 * 帳款月份 = 單據日期的年月(REQ-20260809002)。
 *
 * 刻意不開自己的欄位:需求要的就是「表單建立時的年與月」,而那已經是 `txnDate`
 * (訪談快捷建單時用的是訪談日期)。開一個能跟 `txnDate` 講不同故事的欄位,
 * 對帳時只會多一個要問「以哪個為準」的地方。
 *
 * 這裡可以安全地切字串:`txnDate` 是後端從 `@db.Date` 產出的 `YYYY-MM-DD`
 * 日期字串,不是帶時區的 ISO timestamp,不會有 CLAUDE.md §6.1 那種少一天的問題。
 */
function billingPeriod(txnDate: string): string | null {
  return /^\d{4}-\d{2}/.test(txnDate) ? txnDate.slice(0, 7) : null;
}

/** 一格:標籤 + 值。值為空時留白(給現場手寫),不要印「—」佔住格子 */
function Cell({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex border-b border-black last:border-b-0 min-h-[2rem]">
      <span className="w-28 shrink-0 border-r border-black px-2 py-1 text-xs bg-slate-50">
        {label}
      </span>
      <span className="px-2 py-1 break-all">{value ?? ''}</span>
    </div>
  );
}

function Amount({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-sm">{label}</dt>
      <dd className="font-mono">{formatMoney(value)}</dd>
    </div>
  );
}
